/**
 * Platform Settings Service
 * Loads and caches platform_settings from Supabase for use across the app.
 * Used for dynamic labels (e.g. "Service fee (€2.90)") and client-side calculations
 * (VAT, commission preview). RLS allows SELECT for all users.
 */

import { Injectable, inject, signal, computed } from '@angular/core';
import { from } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { getSupabaseClient } from '../supabase/supabase.client';
import type { CommissionTier } from '../../models/user.model';

export interface PlatformSettingsMap {
  client_service_fee?: number;
  vat_rate?: number;
  cleaner_fee_promotional?: number;
  cleaner_fee_bronze?: number;
  cleaner_fee_silver?: number;
  cleaner_fee_gold?: number;
  promotional_tier_threshold?: number;
  bronze_tier_threshold?: number;
  silver_tier_threshold?: number;
  gold_tier_threshold?: number;
  cleaner_request_digest_enabled?: boolean;
  /** One or more local HH:MM send times (Europe/Brussels). Legacy single string still accepted on read. */
  cleaner_request_digest_time?: string | string[];
  [key: string]: unknown;
}

const DEFAULTS: PlatformSettingsMap = {
  client_service_fee: 2.9,
  vat_rate: 0.21,
  cleaner_fee_promotional: 0,
  cleaner_fee_bronze: 0.15,
  cleaner_fee_silver: 0.1,
  cleaner_fee_gold: 0.08,
  promotional_tier_threshold: 3,
  bronze_tier_threshold: 8,
  silver_tier_threshold: 39,
  gold_tier_threshold: 999999,
  cleaner_request_digest_enabled: true,
  cleaner_request_digest_time: ['18:00'],
};

@Injectable({
  providedIn: 'root',
})
export class PlatformSettingsService {
  private supabase = getSupabaseClient();

  private rawSettings = signal<Record<string, unknown>>({});
  private loaded = signal(false);

  /** Fixed client service fee in EUR (e.g. 2.90). */
  clientServiceFee = computed(() => this.getNumber('client_service_fee') ?? DEFAULTS.client_service_fee!);

  /** VAT rate as decimal (e.g. 0.21 for 21%). */
  vatRate = computed(() => this.getNumber('vat_rate') ?? DEFAULTS.vat_rate!);

  /** Cleaner fee by tier (decimal). */
  cleanerFeePromotional = computed(() => this.getNumber('cleaner_fee_promotional') ?? DEFAULTS.cleaner_fee_promotional!);
  cleanerFeeBronze = computed(() => this.getNumber('cleaner_fee_bronze') ?? DEFAULTS.cleaner_fee_bronze!);
  cleanerFeeSilver = computed(() => this.getNumber('cleaner_fee_silver') ?? DEFAULTS.cleaner_fee_silver!);
  cleanerFeeGold = computed(() => this.getNumber('cleaner_fee_gold') ?? DEFAULTS.cleaner_fee_gold!);

  /** Tier thresholds (max completed jobs for that tier band). */
  promotionalTierThreshold = computed(
    () => this.getNumber('promotional_tier_threshold') ?? DEFAULTS.promotional_tier_threshold!,
  );
  bronzeTierThreshold = computed(() => this.getNumber('bronze_tier_threshold') ?? DEFAULTS.bronze_tier_threshold!);
  silverTierThreshold = computed(() => this.getNumber('silver_tier_threshold') ?? DEFAULTS.silver_tier_threshold!);
  goldTierThreshold = computed(() => this.getNumber('gold_tier_threshold') ?? DEFAULTS.gold_tier_threshold!);

  /** Client service fee formatted for display (e.g. 2.9). */
  clientServiceFeeDisplay = computed(() => this.round2(this.clientServiceFee()));

  /** VAT as percentage for display (e.g. 21). */
  vatPercentDisplay = computed(() => this.percentDisplay(this.vatRate()));

  /** Whether settings have been loaded at least once. */
  isLoaded = computed(() => this.loaded());

  /**
   * Get cleaner commission rate for a tier (decimal, e.g. 0.15 for 15%).
   */
  getCleanerFeeRate(tier: CommissionTier): number {
    switch (tier) {
      case 'promotional':
        return this.cleanerFeePromotional();
      case 'gold':
        return this.cleanerFeeGold();
      case 'silver':
        return this.cleanerFeeSilver();
      case 'bronze':
      default:
        return this.cleanerFeeBronze();
    }
  }

  /**
   * Get cleaner commission percentage for display (e.g. 15).
   */
  getCleanerFeePercentDisplay(tier: CommissionTier): number {
    return this.percentDisplay(this.getCleanerFeeRate(tier));
  }

  /**
   * Tier range text for display (completed jobs), aligned with DB tier logic.
   */
  getTierRangeDisplay(tier: CommissionTier): string {
    const promo = this.promotionalTierThreshold();
    const bronze = this.bronzeTierThreshold();
    const silver = this.silverTierThreshold();
    switch (tier) {
      case 'promotional':
        return `0–${promo} jobs`;
      case 'bronze':
        return `${promo + 1}–${bronze} jobs`;
      case 'silver':
        return `${bronze + 1}–${silver} jobs`;
      case 'gold':
        return `${silver + 1}+ jobs`;
    }
  }

  /**
   * Resolve commission tier from job count (same breakpoints as create_job_on_quote_accept /
   * update_cleaner_job_count). Pass projected total (existing non-cancelled jobs + 1) for quote preview.
   */
  commissionTierFromJobCount(totalJobsCompleted: number): CommissionTier {
    const n = Math.max(0, Math.floor(totalJobsCompleted));
    const promo = this.promotionalTierThreshold();
    const bronze = this.bronzeTierThreshold();
    const silver = this.silverTierThreshold();
    if (n > silver) return 'gold';
    if (n > bronze) return 'silver';
    if (n > promo) return 'bronze';
    return 'promotional';
  }

  /** Load platform settings from Supabase. Call once at app init or when needed. */
  load(): void {
    from(
      this.supabase.from('platform_settings').select('key, value').order('key')
    ).pipe(
      map(({ data, error }) => {
        if (error) {
          console.warn('PlatformSettingsService: failed to load', error);
          return {};
        }
        const map: Record<string, unknown> = {};
        (data || []).forEach((row: { key: string; value: unknown }) => {
          map[row.key] = row.value;
        });
        return map;
      }),
      catchError((err) => {
        console.warn('PlatformSettingsService: load error', err);
        return from([{}]);
      }),
      tap((map) => {
        this.rawSettings.set(map);
        this.loaded.set(true);
      })
    ).subscribe();
  }

  /** Refresh settings from Supabase. */
  refresh(): void {
    this.load();
  }

  private getNumber(key: string): number | undefined {
    const v = this.rawSettings()[key];
    if (v === undefined || v === null) return undefined;
    const n = Number(v);
    return Number.isNaN(n) ? undefined : n;
  }

  private percentDisplay(decimal: number): number {
    return Math.round(decimal * 1000) / 10;
  }

  private round2(n: number): number {
    return Math.round(n * 100) / 100;
  }
}
